import { useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

import useAuthStore from '@entities/auth/model/useAuthStore';
import { DOCUMENT_STATE } from '@entities/approval/constants';
import {
  useDocumentCommentListQuery,
  useDocumentExecutorListQuery,
  useDocumentFileListQuery,
  useDocumentReceiverListQuery,
  useDocumentReferenceDocsQuery,
  useDocumentContentImageQuery,
} from '@features/document-write/hooks';
import Alert from '@shared/components/atoms/Alert';
import MobileConfirm from '@shared/components/atoms/MobileConfirm';
import MobileModalHeader from '@shared/components/atoms/MobileModalHeader';
import MobileInputModal from '@shared/components/atoms/MobileInputModal';
import MobileToast from '@shared/components/atoms/MobileToast';
import useAlert from '@shared/hooks/useAlert';
import useToast from '@shared/hooks/useToast';
import LoadingOverlay from '@shared/components/atoms/LoadingOverlay/LoadingOverlay';

import {
  useDocumentDetail,
  useEmployeeQueries,
  useDocumentActions,
} from '../../DocumentDetailModal/hooks';
import {
  transformDocumentInfo,
  transformReferenceDocuments,
  transformAttachments,
} from '../../DocumentDetailModal/utils';

import MobileDocumentBody from './MobileDocumentBody';
import MobileAttachmentSection from './MobileAttachmentSection';
import MobileCommentDetailModal from './MobileCommentDetailModal';
import MobileApprovalLineCarousel from './MobileApprovalLineCarousel';
import MobileDocumentInfoSection from './MobileDocumentInfoSection';
import MobileTitleSection from './MobileTitleSection';
import MobilePersonListBottomSheet from './MobilePersonListBottomSheet';

import styles from './MobileDocumentDetail.module.scss';

/**
 * 모바일 문서 상세 보기 컴포넌트
 * - 모바일 화면 (767px 이하)에서 사용
 * - PC 버전과 달리 CKEditor 대신 문서 본문 이미지 사용
 * - 풀스크린 레이아웃
 */
function MobileDocumentDetail({ isOpen, onClose, docIdx, userRole = 'approver', tabType }) {
  const user = useAuthStore(function (state) {
    return state.user;
  });
  const enumber = useAuthStore(function (state) {
    return state.enumber;
  });
  const companyCode = useAuthStore(function (state) {
    const code = state.companyCode;
    return code ? Number(code) : null;
  });

  // 토스트 hook
  const { toastState, showToast, hideToast, hasShownToast, setHasShownToast, resetToast } =
    useToast();

  // 바텀시트 상태 (참조자/시행자 목록)
  const [isPersonListOpen, setIsPersonListOpen] = useState(false);
  const [personListTitle, setPersonListTitle] = useState('');
  const [personListData, setPersonListData] = useState([]);

  // 역할 및 탭 확인
  const isApprover = userRole === 'approver';
  const isApprovalIngTab = tabType === 'approvalIng';
  const isDrafter = userRole === 'drafter';

  // 문서 상세 로딩
  const { isLoading, docData, error } = useDocumentDetail(docIdx, isOpen, userRole);

  // 문서 본문 이미지 로딩 (모바일 전용)
  const { data: contentImageData, isLoading: isContentImageLoading } = useDocumentContentImageQuery(
    docIdx,
    isOpen && !!docData,
  );

  // 문서 로딩 에러 전용 Alert
  const {
    alertState: loadErrorAlertState,
    showAlert: showLoadErrorAlert,
    closeAlert: closeLoadErrorAlert,
  } = useAlert();

  function getLoadErrorMessage(rawError) {
    const message = typeof rawError === 'string' ? rawError : '';

    if (message.includes('Error occurred while trying to proxy')) {
      return '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.';
    }

    if (message.includes('유효하지 않은 문서 인덱스')) {
      return message;
    }

    return '문서를 불러오는 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
  }

  useEffect(
    function () {
      if (!isOpen || !error) {
        return;
      }

      showLoadErrorAlert(getLoadErrorMessage(error), function () {
        onClose();
      });
    },
    [error, isOpen, onClose, showLoadErrorAlert],
  );

  // 문서 상태 확인 (API에서 문자열로 올 수 있으므로 Number로 비교)
  const documentState = docData?.state != null ? Number(docData.state) : undefined;
  const isReturned = documentState === DOCUMENT_STATE.RETURN;

  // 수정요청: 문서 전체 state가 6이거나, 결재라인 중 하나라도 state가 6인 경우
  // - 수정요청은 결재자(예: sign_2, sign_3)가 하므로 sign_1_state가 아닌 다른 라인이 6일 수 있음
  const approvalLineStates = docData
    ? [
        docData.sign_1_state,
        docData.sign_2_state,
        docData.sign_3_state,
        docData.sign_4_state,
        docData.sign_5_state,
        docData.check_1_state,
        docData.check_2_state,
        docData.check_3_state,
        docData.check_4_state,
        docData.agreement_state,
      ].filter((s) => s != null)
    : [];
  const hasModificationRequestInLine = approvalLineStates.some(
    (s) => Number(s) === DOCUMENT_STATE.RETURNEDIT,
  );
  const isModificationRequested =
    documentState === DOCUMENT_STATE.RETURNEDIT || hasModificationRequestInLine;

  // 로그인한 사용자가 문서의 기안자인지 확인 (스토어의 enumber 사용 - user 객체 안에 없을 수 있음)
  const isDocumentOwner = useMemo(
    function () {
      if (!docData || (enumber == null && user?.enumber == null)) {
        return false;
      }
      const drafterEnumber = Number(docData.sign_1);
      const currentUserEnumber = Number(enumber ?? user?.enumber);
      return drafterEnumber === currentUserEnumber;
    },
    [docData?.sign_1, enumber, user?.enumber],
  );

  // 수정/삭제 버튼: 상신한 사용자(기안자)만 표시
  // - 수정요청은 결재자가 하고, 수정은 로그인한 기안자 본인이 해야 함
  // - 기안자 본인이고 문서가 반려 또는 수정요청 상태일 때만 표시 (어디서 열었는지와 무관)
  const showModifyDeleteButtons = isDocumentOwner && (isReturned || isModificationRequested);

  // 코멘트 수정/삭제 가능 여부 확인
  // 결재완료(3), 반려(4), 수정요청(6) - 결재 프로세스가 끝난 상태에서 코멘트 수정/삭제 가능
  // API 응답이 문자열로 올 수 있어 Number()로 비교
  const isEditable = useMemo(
    function () {
      if (!docData) {
        return false;
      }
      const state = Number(docData.state);
      return (
        state === DOCUMENT_STATE.SIGNED ||
        state === DOCUMENT_STATE.RETURN ||
        state === DOCUMENT_STATE.RETURNEDIT
      );
    },
    [docData?.state],
  );

  // 참조자/시행자/참조문서/첨부파일/코멘트 조회
  const { data: receiverListData } = useDocumentReceiverListQuery(docIdx, !!isOpen);
  const { data: executorListData } = useDocumentExecutorListQuery(docIdx, !!isOpen);
  const { data: referenceDocListData } = useDocumentReferenceDocsQuery(docIdx, !!isOpen);
  const { data: fileListData } = useDocumentFileListQuery(docIdx, !!isOpen);
  const { data: commentListData, isFetched: isCommentFetched } = useDocumentCommentListQuery(
    docIdx,
    !!isOpen,
  );

  // 코멘트가 있는 경우 토스트 표시
  useEffect(() => {
    if (
      isOpen &&
      isCommentFetched &&
      !hasShownToast &&
      Array.isArray(commentListData) &&
      commentListData.length > 0
    ) {
      showToast('코멘트가 있는 문서입니다.');
      setHasShownToast(true);
    }
  }, [isOpen, isCommentFetched, commentListData, hasShownToast, showToast]);

  // 모달이 닫힐 때 토스트 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      resetToast();
    }
  }, [isOpen, resetToast]);
  // 직원 정보 조회
  const { employeeMap, recipientEmployeeMap, executorEmployeeMap } = useEmployeeQueries({
    docData,
    companyCode,
    receiverListData,
    executorListData,
  });

  // 문서 정보 변환
  const documentInfo = useMemo(
    function () {
      return transformDocumentInfo(
        docData,
        employeeMap,
        receiverListData,
        executorListData,
        recipientEmployeeMap,
        executorEmployeeMap,
      );
    },
    [
      docData,
      employeeMap,
      receiverListData,
      executorListData,
      recipientEmployeeMap,
      executorEmployeeMap,
    ],
  );

  // 참조문서 변환
  const referenceDocuments = useMemo(
    function () {
      return transformReferenceDocuments(referenceDocListData);
    },
    [referenceDocListData],
  );

  // 첨부파일 변환
  const attachments = useMemo(
    function () {
      return transformAttachments(fileListData);
    },
    [fileListData],
  );

  // 액션 핸들러
  const {
    alertState,
    isInputModalOpen,
    inputModalValue,
    isRejectModalOpen,
    rejectModalValue,
    isApproveConfirmOpen,
    isCommentModalOpen,
    commentModalValue,
    isCommentDetailModalOpen,
    selectedComment,
    isCommentEditModalOpen,
    commentEditValue,
    stackedDocIds,
    setInputModalValue,
    setRejectModalValue,
    setCommentModalValue,
    setCommentEditValue,
    showAlert,
    closeAlert,
    handleDocumentInfoChange,
    handleFileDownload,
    handleRequestModification,
    handleInputModalConfirm,
    handleInputModalCancel,
    handleReject,
    handleRejectModalConfirm,
    handleRejectModalCancel,
    handleApprove,
    handleApproveConfirm,
    handleApproveCancel,
    handleCommentWrite,
    handleCommentModalConfirm,
    handleCommentModalCancel,
    handleCommentClick,
    handleCommentDetailClose,
    handleCommentModify,
    handleCommentEditConfirm,
    handleCommentEditCancel,
    handleCommentDelete,
    handleModify,
    handleDelete,
    handleReferenceClick,
    handleStackedClose,
    isDownloading,
  } = useDocumentActions({
    docIdx,
    docData,
    documentInfo,
    referenceDocuments,
    attachments,
    onClose,
  });

  // 수정요청 확인 핸들러 래핑 (빈 값일 때 토스트 표시)
  function handleInputModalConfirmWithToast() {
    if (!inputModalValue.trim()) {
      showToast('수정요청 사유를 입력해주세요.');
      return;
    }
    handleInputModalConfirm();
  }

  // 반려 확인 핸들러 래핑 (빈 값일 때 토스트 표시)
  function handleRejectModalConfirmWithToast() {
    if (!rejectModalValue.trim()) {
      showToast('반려 사유를 입력해주세요.');
      return;
    }
    handleRejectModalConfirm();
  }

  // 코멘트 작성 확인 핸들러 래핑 (빈 값일 때 토스트 표시)
  function handleCommentModalConfirmWithToast() {
    if (!commentModalValue.trim()) {
      showToast('코멘트 내용을 입력해주세요.');
      return;
    }
    handleCommentModalConfirm();
  }

  // 코멘트 수정 확인 핸들러 래핑 (빈 값일 때 토스트 표시)
  function handleCommentEditConfirmWithToast() {
    if (!commentEditValue.trim()) {
      showToast('코멘트 내용을 입력해주세요.');
      return;
    }
    handleCommentEditConfirm();
  }

  // 참조자 클릭 핸들러 (바텀시트 열기)
  function handleReceiverClick(receivers) {
    setPersonListTitle('참조자');
    setPersonListData(receivers);
    setIsPersonListOpen(true);
  }

  // 시행자 클릭 핸들러 (바텀시트 열기)
  function handleExecutorClick(executors) {
    setPersonListTitle('시행자');
    setPersonListData(executors);
    setIsPersonListOpen(true);
  }

  // 바텀시트 닫기 핸들러
  function handlePersonListClose() {
    setIsPersonListOpen(false);
    setPersonListTitle('');
    setPersonListData([]);
  }

  // 코멘트 소유자 여부
  const isCommentOwner = useMemo(
    function () {
      if (!isEditable) {
        return false;
      }

      if (selectedComment && enumber) {
        const commentEnumber = selectedComment.user || selectedComment.enumber;
        const commentEnumberNum = Number(commentEnumber);
        const userEnumberNum = Number(enumber);
        const isEnumberMatch = commentEnumberNum === userEnumberNum;

        return isEnumberMatch && isEditable;
      }

      return false;
    },
    [selectedComment, enumber, isEditable],
  );

  // 본문 이미지 URL
  const contentImageUrl = useMemo(
    function () {
      if (!contentImageData?.fileURL) {
        return null;
      }
      // 상대 경로인 경우 API 서버 URL 추가
      if (contentImageData.fileURL.startsWith('/')) {
        return `${process.env.REACT_APP_API_URL || ''}${contentImageData.fileURL}`;
      }
      return contentImageData.fileURL;
    },
    [contentImageData],
  );

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className={styles.container}>
      {/* 헤더 */}
      <MobileModalHeader title={docData?.title || '문서 상세'} onBack={onClose} />

      {/* 토스트 알림 (코멘트 알림 및 검증 메시지) */}
      <MobileToast
        message={toastState.message}
        isVisible={toastState.isVisible}
        onClose={hideToast}
        duration={2500}
      />

      {/* 본문 영역 */}
      <div className={styles.content}>
        {/* 로딩 상태 */}
        {(isLoading || isContentImageLoading) && (
          <div className={styles.loadingContainer}>
            <div className={styles.circularProgress} role="status" aria-label="문서 로딩 중" />
            <div className={styles.loadingMessage}>문서를 불러오는 중...</div>
          </div>
        )}
        {/* 다운로드 로딩 상태 */}
        {isDownloading && <LoadingOverlay message="파일 다운로드 중..." />}

        {/* 문서 내용 */}
        {!isLoading && !error && docData && documentInfo && (
          <>
            {/* 결재라인 캐러셀 */}
            <MobileApprovalLineCarousel documentInfo={documentInfo} docIdx={docIdx} />

            <div className={styles.sectionGap} />

            {/* 기안 정보 및 참조/시행 */}
            <MobileDocumentInfoSection
              documentInfo={documentInfo}
              docData={docData}
              receivers={documentInfo?.recipients || []}
              executors={documentInfo?.executors || []}
              onReceiverClick={handleReceiverClick}
              onExecutorClick={handleExecutorClick}
            />

            <div className={styles.sectionGap} />

            {/* 제목 */}
            <MobileTitleSection title={docData?.doc_title || docData?.title || ''} />

            <div className={styles.sectionGap} />

            {/* 문서 본문 이미지 */}
            <MobileDocumentBody
              imageUrl={contentImageUrl}
              isLoading={isContentImageLoading}
              title={docData?.title}
            />

            {/* 요약 및 비고, 첨부파일, 참조문서, 코멘트 */}
            <MobileAttachmentSection
              summaryNote={docData?.summary || docData?.note || docData?.summary_note || ''}
              attachments={attachments}
              referenceDocuments={referenceDocuments}
              comments={commentListData || []}
              showCommentWriteButton={isApprover && isApprovalIngTab}
              onFileClick={handleFileDownload}
              onReferenceDocClick={handleReferenceClick}
              onCommentClick={handleCommentClick}
              onCommentWriteClick={handleCommentWrite}
            />
          </>
        )}
      </div>

      {/* 하단 액션 버튼 - PC 버전과 동일한 조건으로 표시 */}
      {/* 결재자+미결함 탭이거나 기안자+반려함 문서일 때만 footer 표시 */}
      {!isLoading &&
        !error &&
        docData &&
        ((isApprover && isApprovalIngTab) || showModifyDeleteButtons) && (
          <div className={styles.footer}>
            {/* 결재자 - 미결함 탭: 결재/반려/수정요청 버튼 */}
            {isApprover && isApprovalIngTab && (
              <>
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={handleRequestModification}
                >
                  수정요청
                </button>
                <button type="button" className={styles.actionButton} onClick={handleReject}>
                  반려
                </button>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.primaryButton}`}
                  onClick={handleApprove}
                >
                  결재
                </button>
              </>
            )}

            {/* 기안자 - 반려 문서만 수정 버튼 표시 (모바일에서는 수정요청 문서는 수정 버튼 미표시, 기안자는 삭제 권한 없음) */}
            {showModifyDeleteButtons && isReturned && (
              <button type="button" className={styles.actionButton} onClick={handleModify}>
                수정
              </button>
            )}
          </div>
        )}

      {/* 알림 모달 - closeAlert 내부에서 alertState.onConfirm(=onClose=closeDetailModal) 호출 */}
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
      <Alert
        isOpen={loadErrorAlertState.isOpen}
        message={loadErrorAlertState.message}
        onClose={closeLoadErrorAlert}
      />

      {/* 수정요청 모달 (바텀시트) */}
      <MobileInputModal
        isOpen={isInputModalOpen}
        title="수정요청 사유를 입력해주세요."
        value={inputModalValue}
        onChange={setInputModalValue}
        onConfirm={handleInputModalConfirmWithToast}
        onCancel={handleInputModalCancel}
        placeholder="수정요청 사유를 입력해주세요."
        maxLength={500}
        onExceedMaxLength={() => showToast('500자까지 입력 가능합니다.')}
      />

      {/* 반려 모달 (바텀시트) */}
      <MobileInputModal
        isOpen={isRejectModalOpen}
        title="반려 사유를 입력해주세요."
        value={rejectModalValue}
        onChange={setRejectModalValue}
        onConfirm={handleRejectModalConfirmWithToast}
        onCancel={handleRejectModalCancel}
        placeholder="반려 사유를 입력해주세요."
        maxLength={500}
        onExceedMaxLength={() => showToast('500자까지 입력 가능합니다.')}
      />

      {/* 결재 확인 모달 (모바일 스타일) */}
      <MobileConfirm
        isOpen={isApproveConfirmOpen}
        message="결재하시겠습니까?"
        onConfirm={handleApproveConfirm}
        onCancel={handleApproveCancel}
        confirmText="예"
        cancelText="아니오"
      />

      {/* 코멘트 작성 모달 (바텀시트) */}
      <MobileInputModal
        isOpen={isCommentModalOpen}
        title="코멘트 내용을 입력해 주세요."
        value={commentModalValue}
        onChange={setCommentModalValue}
        onConfirm={handleCommentModalConfirmWithToast}
        onCancel={handleCommentModalCancel}
        placeholder="내용을 입력해주세요."
        maxLength={500}
        onExceedMaxLength={() => showToast('500자까지 입력 가능합니다.')}
      />

      {/* 코멘트 상세 모달 */}
      <MobileCommentDetailModal
        isOpen={isCommentDetailModalOpen}
        comment={selectedComment}
        isOwner={isCommentOwner}
        onClose={handleCommentDetailClose}
        onModify={handleCommentModify}
        onDelete={handleCommentDelete}
      />

      {/* 코멘트 수정 모달 (바텀시트) */}
      <MobileInputModal
        isOpen={isCommentEditModalOpen}
        title="코멘트 내용을 수정해 주세요."
        value={commentEditValue}
        onChange={setCommentEditValue}
        onConfirm={handleCommentEditConfirmWithToast}
        onCancel={handleCommentEditCancel}
        placeholder="내용을 입력해주세요."
        maxLength={500}
        onExceedMaxLength={() => showToast('500자까지 입력 가능합니다.')}
      />

      {/* 중첩된 참조문서 모달 렌더링 */}
      {stackedDocIds.map(function (id, index) {
        return (
          <MobileDocumentDetail
            key={`stacked-mobile-${index}-${id}`}
            isOpen
            onClose={handleStackedClose}
            docIdx={id}
            userRole="reference"
            tabType="reference"
          />
        );
      })}

      {/* 참조자/시행자 목록 바텀시트 */}
      <MobilePersonListBottomSheet
        isOpen={isPersonListOpen}
        title={personListTitle}
        persons={personListData}
        onClose={handlePersonListClose}
      />
    </div>,
    document.body,
  );
}

export default MobileDocumentDetail;
